/**
 * Phase 5 — offline key/value storage.
 *
 * Native shell -> @capacitor/filesystem (Directory.Data), so cached Desk data
 * and the pending write queue survive app restarts and low-memory kills.
 * Browser/PWA -> localStorage, so the same API works on the web without any
 * behaviour change for existing users.
 *
 * All functions fail soft: storage problems must never break a screen.
 */
import { isNativeApp } from '../platform';

const DIR = 'hibane-offline';
const WEB_PREFIX = 'hibane_offline:';

const filePath = (key: string) => `${DIR}/${encodeURIComponent(key)}.json`;

async function fs() {
  const mod = await import('@capacitor/filesystem');
  return mod;
}

export async function readRaw(key: string): Promise<string | null> {
  if (!isNativeApp()) {
    try {
      return localStorage.getItem(WEB_PREFIX + key);
    } catch {
      return null;
    }
  }
  try {
    const { Filesystem, Directory, Encoding } = await fs();
    const res = await Filesystem.readFile({
      path: filePath(key),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    return typeof res.data === 'string' ? res.data : null;
  } catch {
    return null;
  }
}

export async function writeRaw(key: string, value: string): Promise<void> {
  if (!isNativeApp()) {
    try {
      localStorage.setItem(WEB_PREFIX + key, value);
    } catch {
      /* quota — ignore */
    }
    return;
  }
  try {
    const { Filesystem, Directory, Encoding } = await fs();
    await Filesystem.writeFile({
      path: filePath(key),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      data: value,
      recursive: true,
    });
  } catch {
    /* ignore */
  }
}

export async function removeRaw(key: string): Promise<void> {
  if (!isNativeApp()) {
    try {
      localStorage.removeItem(WEB_PREFIX + key);
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    const { Filesystem, Directory } = await fs();
    await Filesystem.deleteFile({ path: filePath(key), directory: Directory.Data });
  } catch {
    /* ignore */
  }
}

export async function readJson<T>(key: string): Promise<T | null> {
  const raw = await readRaw(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export async function writeJson(key: string, value: unknown): Promise<void> {
  try {
    await writeRaw(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
